<script setup>
// Leave-the-meet confirm (mockup meet-leave.html, E5): future RSVPs are
// cancelled NOW, with cancellation tiers applied provisionally where inside a
// window (waived if the spot refills, E17); unsettled charges survive.
// The per-date outcome rows are computed from the upcoming occurrences whose
// detail (myAttendance) the hub has loaded — dates whose status is unknown fall
// back to the generic consequences copy.
import {computed, ref, watch} from 'vue';
import TlDialog from '~/components/TlDialog.vue';
import {useLeaveEntity} from '~/composables/useLeaveEntity';
import {cancellationTermsFor, useMeetFormat, AttendanceStatus} from '~/composables/useMeetFormat';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  meet: {type: Object, required: true},
  // [{occurrence, myAttendance}] — upcoming dates with my loaded RSVP rows
  myUpcoming: {type: Array, default: () => []},
});
const emit = defineEmits(['update:modelValue', 'left']);

const {leaveMeet, leavePending, leaveErrors} = useLeaveEntity();
const {occurrenceDay, activeTier, isPaid, money} = useMeetFormat();

watch(() => props.modelValue, (open) => { if (open) leaveErrors.value = {}; });

// My live RSVPs among the upcoming dates (the rows leaving will cancel)
const affected = computed(() => props.myUpcoming
    .filter(({myAttendance}) => myAttendance && [
      AttendanceStatus.Attending,
      AttendanceStatus.Waitlisted,
      AttendanceStatus.PromotionOffered,
    ].includes(myAttendance.status))
    .map(({occurrence, myAttendance}) => {
      const waiting = myAttendance.status !== AttendanceStatus.Attending;
      // Each date is priced from what cancelling that booking would cost
      // (its agreed tiers and fee when the API sends them).
      const terms = cancellationTermsFor(props.meet, occurrence);
      const tier = !waiting && isPaid(terms) ? activeTier(terms, occurrence) : null;
      return {
        occurrence,
        label: waiting
            ? `${occurrenceDay(occurrence)} · waitlisted`
            : tier
                ? `${occurrenceDay(occurrence)} · inside the ${tierWindowLabel(tier)} window`
                : `${occurrenceDay(occurrence)} · outside all windows`,
        outcome: tier
            ? `${tier.percent}% · ${money(Math.round(terms.feeMinor * tier.percent / 100), terms.currency)}`
            : 'Free',
        free: !tier,
      };
    }));

const tierWindowLabel = (tier) => tier.label.replace(/ before$/, '').toLowerCase();

const provisionalTotalMinor = computed(() => props.myUpcoming.reduce((sum, {occurrence, myAttendance}) => {
  const terms = cancellationTermsFor(props.meet, occurrence);
  if (!myAttendance || myAttendance.status !== AttendanceStatus.Attending || !isPaid(terms)) return sum;
  const tier = activeTier(terms, occurrence);
  return tier ? sum + Math.round(terms.feeMinor * tier.percent / 100) : sum;
}, 0));

const confirm = async () => {
  try {
    await leaveMeet(props.meet.id);
    emit('update:modelValue', false);
    emit('left');
  } catch {
    // surfaced via leaveErrors
  }
};
</script>

<template>
  <tl-dialog
      :model-value="modelValue"
      :title="`Leave ${meet.name}?`"
      :max-width="460"
      @update:model-value="emit('update:modelValue', $event)"
  >
    <p v-if="affected.length" class="leave-sub">Here's what happens to your upcoming RSVPs:</p>
    <p v-else class="leave-sub">You'll be removed from the meet and will need an invite link or a new request to rejoin.</p>

    <div
        v-for="row in affected"
        :key="row.occurrence.id"
        class="tier-row"
        :class="{'tier-row--free': row.free}"
    >
      <span>{{ row.label }}</span>
      <b>{{ row.outcome }}</b>
    </div>

    <div class="assure" :class="{'assure--warn': provisionalTotalMinor > 0}">
      <v-icon size="17" :icon="provisionalTotalMinor > 0 ? 'mdi-alert-outline' : 'mdi-information-outline'"/>
      <span>
        <template v-if="affected.length">Leaving cancels {{ affected.length === 1 ? 'this RSVP' : 'these RSVPs' }} now.</template>
        <template v-if="provisionalTotalMinor > 0">
          The {{ money(provisionalTotalMinor, meet.currency) }} late-cancellation fee applies unless someone
          takes your spot (it's offered to the waitlist immediately).
        </template>
        <template v-if="isPaid(meet)"> Unsettled charges remain payable.</template>
        You can rejoin any time with an invite link.
      </span>
    </div>

    <crud-error-display :errors="leaveErrors" class="mt-3"/>

    <template #actions>
      <v-btn variant="text" @click="emit('update:modelValue', false)">Keep membership</v-btn>
      <v-spacer/>
      <v-btn color="error" variant="flat" :loading="leavePending" @click="confirm">
        {{ provisionalTotalMinor > 0 ? `Leave (${money(provisionalTotalMinor, meet.currency)} if unfilled)` : 'Leave meet' }}
      </v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.leave-sub {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-bottom: 12px;
}

.tier-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border: 1px solid rgb(var(--v-theme-warning));
  background: rgb(var(--v-theme-warningTint));
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 500;

  & + .tier-row {
    margin-top: 6px;
  }

  b {
    font-weight: 700;
  }

  &--free {
    border-color: rgb(var(--v-theme-success));
    background: rgb(var(--v-theme-successTint));
  }
}

.assure {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgb(var(--v-theme-successTint));
  color: rgb(var(--v-theme-charcoal));
  font-size: 11.5px;
  line-height: 1.55;

  .v-icon {
    color: rgb(var(--v-theme-success));
    flex-shrink: 0;
    margin-top: 1px;
  }

  &--warn {
    background: rgb(var(--v-theme-warningTint));

    .v-icon {
      color: rgb(var(--v-theme-warning));
    }
  }
}
</style>
